import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDailySummary, getVitals, getHealthTrends, getHealthScore, getStreaks, getCorrelations, getMyProfile, getWeeklyDigest, getPerformanceWindow, logInvestmentDecision } from '../services/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

interface Trend {
  metric: string;
  label: string;
  recent_avg: number | null;
  baseline_avg: number | null;
  direction: 'up' | 'down' | 'stable' | 'insufficient_data';
  unit: string;
}

interface HealthFlag {
  severity: 'critical' | 'high' | 'medium' | 'low';
  metric: string;
  value: string;
  message: string;
}

interface HealthScore {
  score: number | null;
  grade: string;
  color: string;
  flags: HealthFlag[];
  based_on_date?: string;
}

interface Streaks {
  vitals_streak: number;
  med_adherence_pct: number | null;
  journal_days_7: number;
  total_vitals_days: number;
}

interface CorrelationInsight {
  finding: string;
  recommendation: string;
  confidence: 'high' | 'medium' | 'low';
  metric_a: string;
  metric_b: string;
}

const DIRECTION_COLOR: Record<string, string> = {
  up: 'text-blue-600',
  down: 'text-red-500',
  stable: 'text-emerald-600',
  insufficient_data: 'text-gray-400',
};

const DIRECTION_ICON: Record<string, string> = {
  up: '↑',
  down: '↓',
  stable: '→',
  insufficient_data: '—',
};

const SCORE_COLORS: Record<string, { border: string; bg: string; text: string }> = {
  emerald: { border: 'border-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-600' },
  green:   { border: 'border-green-500',   bg: 'bg-green-50',   text: 'text-green-600' },
  yellow:  { border: 'border-yellow-400',  bg: 'bg-yellow-50',  text: 'text-yellow-500' },
  orange:  { border: 'border-orange-500',  bg: 'bg-orange-50',  text: 'text-orange-500' },
  red:     { border: 'border-red-500',     bg: 'bg-red-50',     text: 'text-red-500' },
};

export default function DashboardPage() {
  const { currentUser, userProfile } = useAuth();
  const [summary, setSummary] = useState<string | null>(null);
  const [vitals, setVitals] = useState<any[]>([]);
  const [trends, setTrends] = useState<Trend[]>([]);
  const [healthScore, setHealthScore] = useState<HealthScore | null>(null);
  const [streaks, setStreaks] = useState<Streaks | null>(null);
  const [correlations, setCorrelations] = useState<CorrelationInsight[]>([]);
  const [correlationMsg, setCorrelationMsg] = useState('');
  const [profile, setProfile] = useState<any>(null);
  const [weeklyDigest, setWeeklyDigest] = useState<any>(null);
  const [perfWindow, setPerfWindow] = useState<any>(null);
  const [perfWindowMsg, setPerfWindowMsg] = useState('');
  const [decisionForm, setDecisionForm] = useState({ description: '', outcome: 'profit', pnl_pct: '', energy_rating: '' });
  const [loggingDecision, setLoggingDecision] = useState(false);
  const [showDecisionForm, setShowDecisionForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();

      // Phase 1: fast data — show the page immediately
      const [vitalsRes, trendsRes, scoreRes, streaksRes, profileRes, perfRes] =
        await Promise.allSettled([
          getVitals(token, 14),
          getHealthTrends(token),
          getHealthScore(token),
          getStreaks(token),
          getMyProfile(token),
          getPerformanceWindow(token),
        ]);

      if (vitalsRes.status === 'fulfilled') setVitals(vitalsRes.value.vitals || []);
      if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.trends || []);
      if (scoreRes.status === 'fulfilled') setHealthScore(scoreRes.value);
      if (streaksRes.status === 'fulfilled') setStreaks(streaksRes.value);
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value);
      if (perfRes.status === 'fulfilled') {
        setPerfWindow(perfRes.value);
        setPerfWindowMsg(perfRes.value.message || '');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }

    // Phase 2: AI calls — load in background after page is visible
    try {
      const token = await currentUser.getIdToken();
      const [summaryRes, correlationsRes, digestRes] = await Promise.allSettled([
        getDailySummary(token),
        getCorrelations(token),
        getWeeklyDigest(token),
      ]);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.summary);
      if (correlationsRes.status === 'fulfilled') {
        setCorrelations(correlationsRes.value.insights || []);
        setCorrelationMsg(correlationsRes.value.message || '');
      }
      if (digestRes.status === 'fulfilled') setWeeklyDigest(digestRes.value.digest);
    } catch {/* AI calls are best-effort */}
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleLogDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !decisionForm.description.trim()) return;
    setLoggingDecision(true);
    try {
      const token = await currentUser.getIdToken();
      await logInvestmentDecision(token, {
        description: decisionForm.description.trim(),
        outcome: decisionForm.outcome,
        pnl_pct: decisionForm.pnl_pct ? parseFloat(decisionForm.pnl_pct) : undefined,
        energy_rating: decisionForm.energy_rating ? parseInt(decisionForm.energy_rating) : undefined,
      });
      setDecisionForm({ description: '', outcome: 'profit', pnl_pct: '', energy_rating: '' });
      setShowDecisionForm(false);
      // Refresh performance window
      const token2 = await currentUser.getIdToken();
      const res = await getPerformanceWindow(token2);
      setPerfWindow(res);
      setPerfWindowMsg(res.message || '');
    } catch {
      // silently ignore
    } finally {
      setLoggingDecision(false);
    }
  };

  // Derived chart data
  const heartRateData = vitals
    .filter((v) => v.heart_rate_bpm)
    .slice(-14)
    .map((v) => ({ date: v.date, hr: v.heart_rate_bpm }));

  const weightData = vitals
    .filter((v) => v.weight_kg)
    .slice(-14)
    .map((v) => ({ date: v.date, weight: v.weight_kg }));

  const sleepData = vitals
    .filter((v) => v.sleep_hours)
    .slice(-14)
    .map((v) => ({ date: v.date, sleep: v.sleep_hours }));

  const bpData = vitals
    .filter((v) => v.systolic_bp && v.diastolic_bp)
    .slice(-14)
    .map((v) => ({ date: v.date, systolic: v.systolic_bp, diastolic: v.diastolic_bp }));

  // Weight goal
  const weightGoal = profile?.weight_goal_kg ? parseFloat(profile.weight_goal_kg) : null;
  const currentWeight = weightData.length > 0 ? weightData[weightData.length - 1].weight : null;
  const startWeight = weightData.length > 0 ? weightData[0].weight : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mr-3" />
        Loading your health dashboard...
      </div>
    );
  }

  const scoreStyle = healthScore ? (SCORE_COLORS[healthScore.color] || SCORE_COLORS.red) : null;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Good {getTimeOfDay()}, {userProfile?.display_name || profile?.displayName || 'there'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">Here's your health snapshot for today.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
      )}

      {/* ── Streak cards ──────────────────────────────────────────────────────── */}
      {streaks && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-extrabold text-orange-500">
              {streaks.vitals_streak > 0 ? `🔥 ${streaks.vitals_streak}` : '—'}
            </p>
            <p className="text-xs text-gray-500 mt-1 font-medium">Day Vitals Streak</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-extrabold text-gray-700">
              {streaks.total_vitals_days}
            </p>
            <p className="text-xs text-gray-500 mt-1 font-medium">Total Days Logged</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className={`text-2xl font-extrabold ${
              streaks.med_adherence_pct === null ? 'text-gray-300' :
              streaks.med_adherence_pct >= 80 ? 'text-emerald-600' :
              streaks.med_adherence_pct >= 50 ? 'text-yellow-500' : 'text-red-500'
            }`}>
              {streaks.med_adherence_pct !== null ? `${streaks.med_adherence_pct}%` : '—'}
            </p>
            <p className="text-xs text-gray-500 mt-1 font-medium">Med Adherence (7d)</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-2xl font-extrabold text-blue-500">
              {streaks.journal_days_7 > 0 ? `✏️ ${streaks.journal_days_7}` : '—'}
            </p>
            <p className="text-xs text-gray-500 mt-1 font-medium">Journal Days (7d)</p>
          </div>
        </div>
      )}

      {/* ── AI Daily Summary ───────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-emerald-600">⚕</span>
          <h2 className="font-semibold text-gray-800">AI Health Summary</h2>
          <span className="ml-auto text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Today</span>
        </div>
        {summary ? (
          <p className="text-gray-700 text-sm leading-relaxed">{summary}</p>
        ) : (
          <p className="text-gray-400 text-sm italic">
            Log some vitals to get your personalized AI health summary.
          </p>
        )}
        <button
          onClick={load}
          className="mt-4 text-xs text-emerald-600 hover:text-emerald-800 transition-colors"
        >
          ↻ Refresh summary
        </button>
      </div>

      {/* ── Health Score ───────────────────────────────────────────────────────── */}
      {healthScore && healthScore.score !== null && scoreStyle && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">Health Score</h2>
            {healthScore.based_on_date && (
              <span className="text-xs text-gray-400">Based on {healthScore.based_on_date}</span>
            )}
          </div>

          <div className="flex items-center gap-6 mb-5">
            <div className={`relative flex-shrink-0 w-24 h-24 rounded-full flex items-center justify-center border-4 ${scoreStyle.border} ${scoreStyle.bg}`}>
              <div className="text-center">
                <p className={`text-3xl font-extrabold leading-none ${scoreStyle.text}`}>{healthScore.score}</p>
                <p className="text-xs text-gray-400 mt-0.5">/ 100</p>
              </div>
            </div>
            <div>
              <p className={`text-2xl font-bold ${scoreStyle.text}`}>{healthScore.grade}</p>
              <p className="text-sm text-gray-500 mt-1">
                {healthScore.flags.length === 0
                  ? 'No risk flags detected — keep it up!'
                  : `${healthScore.flags.length} risk flag${healthScore.flags.length > 1 ? 's' : ''} detected`}
              </p>
            </div>
          </div>

          {healthScore.flags.length > 0 && (
            <div className="space-y-2">
              {healthScore.flags.map((flag, i) => (
                <div key={i} className={`flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm ${
                  flag.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                  flag.severity === 'high' ? 'bg-orange-50 border border-orange-200' :
                  flag.severity === 'medium' ? 'bg-yellow-50 border border-yellow-200' :
                  'bg-gray-50 border border-gray-200'
                }`}>
                  <span className="flex-shrink-0 mt-0.5">
                    {flag.severity === 'critical' ? '🔴' : flag.severity === 'high' ? '🟠' : flag.severity === 'medium' ? '🟡' : 'ℹ️'}
                  </span>
                  <div>
                    <span className="font-semibold text-gray-800">{flag.metric}</span>
                    <span className="text-gray-500 mx-1">·</span>
                    <span className="text-gray-600">{flag.value}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{flag.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Correlation Insights ───────────────────────────────────────────────── */}
      {(correlations.length > 0 || correlationMsg) && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-purple-500">🔬</span>
            <h2 className="font-semibold text-gray-800">Pattern Insights</h2>
            <span className="ml-auto text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">AI Analysis</span>
          </div>

          {correlations.length === 0 ? (
            <p className="text-sm text-gray-400 italic">{correlationMsg || 'Log more vitals and journal entries to unlock pattern insights.'}</p>
          ) : (
            <div className="space-y-4">
              {correlations.map((insight, i) => (
                <div key={i} className="border-l-4 border-purple-200 pl-4">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 bg-purple-50 text-purple-600">
                      {insight.confidence === 'high' ? '★★★' : insight.confidence === 'medium' ? '★★' : '★'}
                    </span>
                    <p className="text-sm text-gray-800 font-medium leading-snug">{insight.finding}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5 pl-8">→ {insight.recommendation}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Weight Goal ────────────────────────────────────────────────────────── */}
      {weightGoal && currentWeight && startWeight && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-800 mb-4">Weight Goal</h2>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-500">Current: <strong className="text-gray-800">{currentWeight} kg</strong></span>
            <span className="text-gray-500">Goal: <strong className="text-emerald-700">{weightGoal} kg</strong></span>
          </div>
          {(() => {
            const totalToLose = startWeight - weightGoal;
            const lost = startWeight - currentWeight;
            const pct = totalToLose !== 0 ? Math.max(0, Math.min(100, (lost / totalToLose) * 100)) : 100;
            const remaining = Math.abs(currentWeight - weightGoal);
            const onTrack = (weightGoal < startWeight) ? currentWeight <= startWeight : currentWeight >= startWeight;
            return (
              <>
                <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                  <div
                    className="h-3 rounded-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  {pct >= 100
                    ? '🎉 Goal reached!'
                    : `${remaining.toFixed(1)} kg remaining · ${Math.round(pct)}% complete`}
                </p>
              </>
            );
          })()}
        </div>
      )}

      {/* ── 14-Day Trends ─────────────────────────────────────────────────────── */}
      {trends.length > 0 && (
        <div>
          <h2 className="font-semibold text-gray-800 mb-3">14-Day Trends</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {trends.map((t) => (
              <div key={t.metric} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">{t.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-gray-900">
                    {t.recent_avg !== null ? Number(t.recent_avg).toFixed(1) : '—'}
                  </span>
                  <span className="text-xs text-gray-400">{t.unit}</span>
                  <span className={`ml-auto text-sm font-semibold ${DIRECTION_COLOR[t.direction]}`}>
                    {DIRECTION_ICON[t.direction]}
                  </span>
                </div>
                {t.baseline_avg !== null && (
                  <p className="text-xs text-gray-400 mt-1">
                    vs {Number(t.baseline_avg).toFixed(1)} {t.unit} (90d avg)
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 14-Day Charts ─────────────────────────────────────────────────────── */}
      {(heartRateData.length > 1 || weightData.length > 1 || sleepData.length > 1 || bpData.length > 1) && (
        <div>
          <h2 className="font-semibold text-gray-800 mb-3">14-Day Charts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {heartRateData.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-700 mb-3">Heart Rate (bpm)</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={heartRateData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} width={30} />
                    <Tooltip formatter={(v: any) => [`${v} bpm`, 'HR']} />
                    <Line type="monotone" dataKey="hr" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {weightData.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Weight (kg){weightGoal ? ` · Goal: ${weightGoal} kg` : ''}
                </p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={weightData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} width={35} />
                    <Tooltip formatter={(v: any) => [`${v} kg`, 'Weight']} />
                    <Line type="monotone" dataKey="weight" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    {weightGoal && (
                      <Line type="monotone" dataKey={() => weightGoal} stroke="#10b981" strokeWidth={1} dot={false} strokeDasharray="4 2" name="goal" />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {sleepData.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-700 mb-3">Sleep (hours)</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={sleepData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} domain={[0, 12]} width={30} />
                    <Tooltip formatter={(v: any) => [`${v}h`, 'Sleep']} />
                    <Line type="monotone" dataKey="sleep" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {bpData.length > 1 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <p className="text-sm font-medium text-gray-700 mb-3">Blood Pressure (mmHg)</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={bpData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} width={30} />
                    <Tooltip formatter={(v: any, name: any) => [`${v} mmHg`, name === 'systolic' ? 'Systolic' : 'Diastolic']} />
                    <Line type="monotone" dataKey="systolic" stroke="#ef4444" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="diastolic" stroke="#f97316" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 mt-2">
                  <span className="flex items-center gap-1.5 text-xs text-gray-400"><span className="inline-block w-4 h-0.5 bg-red-500 rounded"></span>Systolic</span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-400"><span className="inline-block w-4 h-0.5 bg-orange-400 rounded"></span>Diastolic</span>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ── Weekly Executive Board Meeting ────────────────────────────────────── */}
      {weeklyDigest && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1">Sovereign Digest</p>
              <h2 className="text-lg font-bold text-white">Weekly Board Meeting</h2>
              {weeklyDigest.headline && (
                <p className="text-sm text-slate-300 mt-1 italic">"{weeklyDigest.headline}"</p>
              )}
            </div>
            <span className="text-2xl">🏛</span>
          </div>
          <div className="space-y-4">
            {[
              { key: 'physician', icon: '⚕', accent: 'border-emerald-400 text-emerald-400' },
              { key: 'treasurer', icon: '📊', accent: 'border-blue-400 text-blue-400' },
              { key: 'stoic',     icon: '⚖', accent: 'border-amber-400 text-amber-400' },
            ].map(({ key, icon, accent }) => {
              const section = weeklyDigest[key];
              if (!section) return null;
              return (
                <div key={key} className={`border-l-2 ${accent.split(' ')[0]} pl-4`}>
                  <p className={`text-xs font-semibold mb-0.5 ${accent.split(' ')[1]}`}>
                    {icon} {section.name} — <span className="font-normal opacity-75">{section.persona}</span>
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed">{section.message}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Performance Window ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-semibold text-gray-800">Performance Window</h2>
            <p className="text-xs text-gray-500 mt-0.5">HRV & sleep on days you made investment decisions</p>
          </div>
          <button
            onClick={() => setShowDecisionForm((v) => !v)}
            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
          >
            + Log Decision
          </button>
        </div>

        {showDecisionForm && (
          <form onSubmit={handleLogDecision} className="mb-4 p-4 bg-indigo-50 rounded-xl space-y-3 border border-indigo-100">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">What did you decide?</label>
              <input
                type="text"
                value={decisionForm.description}
                onChange={(e) => setDecisionForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="e.g. Bought 200 NVDA at $880"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Outcome</label>
                <select
                  value={decisionForm.outcome}
                  onChange={(e) => setDecisionForm((p) => ({ ...p, outcome: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="profit">Profit</option>
                  <option value="loss">Loss</option>
                  <option value="neutral">Neutral</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">P&L %</label>
                <input
                  type="number" step="0.1"
                  value={decisionForm.pnl_pct}
                  onChange={(e) => setDecisionForm((p) => ({ ...p, pnl_pct: e.target.value }))}
                  placeholder="+4.2"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Energy 1-10</label>
                <input
                  type="number" min="1" max="10"
                  value={decisionForm.energy_rating}
                  onChange={(e) => setDecisionForm((p) => ({ ...p, energy_rating: e.target.value }))}
                  placeholder="7"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loggingDecision || !decisionForm.description.trim()}
              className="w-full py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loggingDecision ? 'Saving…' : 'Save Decision'}
            </button>
          </form>
        )}

        {perfWindow && perfWindow.needs_more_data ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-400">{perfWindowMsg || 'Log at least 3 investment decisions to unlock insights.'}</p>
            <p className="text-xs text-gray-400 mt-1">
              {perfWindow.total_decisions || 0} / 3 logged. Your HRV data for those days will be matched automatically.
            </p>
          </div>
        ) : perfWindow && perfWindow.stats && Object.keys(perfWindow.stats).length > 0 ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {perfWindow.stats.avg_hrv_profit_days != null && (
                <div className="bg-green-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-green-600 font-medium mb-0.5">Avg HRV — Profit Days</p>
                  <p className="text-xl font-bold text-green-700">{perfWindow.stats.avg_hrv_profit_days} ms</p>
                </div>
              )}
              {perfWindow.stats.avg_hrv_loss_days != null && (
                <div className="bg-red-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-red-600 font-medium mb-0.5">Avg HRV — Loss Days</p>
                  <p className="text-xl font-bold text-red-700">{perfWindow.stats.avg_hrv_loss_days} ms</p>
                </div>
              )}
            </div>
            {perfWindow.stats.hrv_edge_ms != null && (
              <p className="text-xs text-gray-500 text-center">
                HRV edge: <strong>{perfWindow.stats.hrv_edge_ms > 0 ? '+' : ''}{perfWindow.stats.hrv_edge_ms} ms</strong> on profitable days
              </p>
            )}
            {(perfWindow.insights || []).map((ins: string, i: number) => (
              <div key={i} className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2">
                <p className="text-xs text-indigo-800">{ins}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-3">
            No decisions logged yet. Click "+ Log Decision" after each investment move.
          </p>
        )}
      </div>

      {/* ── Quick actions ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Log Vitals', href: '/app/vitals', icon: '♥', color: 'bg-red-50 text-red-600 hover:bg-red-100' },
          { label: 'Medications', href: '/app/medications', icon: '💊', color: 'bg-teal-50 text-teal-600 hover:bg-teal-100' },
          { label: 'Ask AI Doctor', href: '/app/community', icon: '⚕', color: 'bg-purple-50 text-purple-600 hover:bg-purple-100' },
          { label: 'Download Report', href: '/app/report', icon: '⬇', color: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' },
        ].map(({ label, href, icon, color }) => (
          <a
            key={label}
            href={href}
            className={`${color} rounded-xl p-4 text-center transition-colors block`}
          >
            <div className="text-2xl mb-1">{icon}</div>
            <p className="text-xs font-medium">{label}</p>
          </a>
        ))}
      </div>
    </div>
  );
}

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
